/* eslint-disable react/no-danger */
import React, {
  useEffect, useState,
} from 'react';
import { animateScroll as scroll } from 'react-scroll';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import { useAuthStore, useAppStore } from '../../stores';
import { useFavoriteButtons } from '../Personalization/Favorite/FavoriteButtonsContext';

import PibPageHeader from './PibPageHeader';
import PibPageContent from './PibPageContent';
import PibPageSkeleton from './PibPageSkeleton';
import FadingContainer from '../FadingContainer/FadingContainer';

import './PibPage.scss';
import './PibPageWrapper.scss';

const auth = getAuthClient();

// get current year
const date = new Date();
const year = date.getFullYear();

const PibPage = () => {
  const [pibData, setPibData] = useState(null);
  const [isContentRendered, setIsContentRendered] = useState(false);
  const [isContentFaded, setIsContentFaded] = useState(false);

  const { communitySlug, drupalNid } = useParams();

  const isAuthed = useAuthStore((state) => state.isAuthed);
  const settings = useAppStore((state) => state.settings);
  const { mode } = settings || {};

  // Extract data from pibData
  const { resident, community, salesCounselor, favorites } = pibData || {};
  const { name, amenitiesServicesHTML } = community || {};

  const handleBackToTopClick = () => {
    scroll.scrollToTop({
      duration: 500,
      smooth: 'easeInOutCubic',
    });
  };

  // Set favorites for map context
  const { setFavorites, setResidenceFavorites } = useFavoriteButtons();

  // Fetch all PIB data in a single optimized API call
  useEffect(() => {
    const fetchPibData = async () => {
      try {
        const params = mode ? { mode } : {};
        const url = buildYourTourApiUrl(`/your_tour_api/pib_page/${drupalNid}`, params);
        const data = await auth.fetchWithAuthentication(url, 'GET', null);

        if (data && !data.error) {
          setPibData(data);

          // Set favorite IDs for map context
          if (data.resident) {
            setFavorites(data.resident.mapFavorites);
            setResidenceFavorites(data.resident.mapResidencesFavorites);
          }
        } else {
          console.error('Error fetching PIB data:', data?.error);
          setPibData({});
        }
      } catch (error) {
        console.error('Error fetching PIB data:', error);
        setPibData({});
      }
    };

    if (isAuthed && drupalNid) {
      fetchPibData();
    }
  }, [isAuthed, drupalNid, mode, setFavorites, setResidenceFavorites]);

  // Set page title.
  // Update page title with community name.
  useEffect(() => {
    document.title = `${name ? `${name} |` : ''} My YourTour`;
  }, [name]);

  // Wait for data before starting fade-in
  useEffect(() => {
    if (pibData && !isContentRendered) {
      const timer = setTimeout(() => {
        setIsContentRendered(true);
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [pibData, isContentRendered]);

  // Track when fade is complete to swap positions
  useEffect(() => {
    if (isContentRendered && !isContentFaded) {
      const timer = setTimeout(() => {
        setIsContentFaded(true);
      }, 500); // Match the CSS transition duration
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [isContentRendered, isContentFaded]);

  // Build the data structure expected by PibPageHeader
  const residentCommunityData = pibData ? {
    community,
    names: resident?.names,
    createdDate: resident?.createdDate,
    changedDate: resident?.changedDate,
    message: resident?.message,
    salesCounselor,
    uuid: resident?.uuid,
    // Include favorite IDs for backwards compatibility
    favoriteResidences: resident?.favoriteResidenceIds || [],
    favoriteAmenities: resident?.favoriteAmenityIds || [],
    favoriteFloorPlans: resident?.favoriteFloorPlanIds || [],
    favoriteMedia: resident?.favoriteMediaIds || [],
    mapResidencesFavorites: resident?.mapResidencesFavorites,
    mapFavorites: resident?.mapFavorites,
  } : null;

  return (
    <FadingContainer>
      <div className="pibPage-wrapper">
        <PibPageSkeleton
          className={classNames('pibPage pibPage-skeleton', {
            'pibPage-skeleton--hidden': isContentFaded,
          })}
        />

        <div
          className={classNames('pibPage pibPage-content', {
            'pibPage-content--fadeIn': isContentRendered,
            'pibPage-content--visible': isContentFaded,
          })}
        >
          <PibPageHeader residentCommunityData={residentCommunityData} />

          <PibPageContent
            communitySlug={communitySlug}
            resourcesData={community?.resources}
            amenitiesServicesHTML={amenitiesServicesHTML}
            initialFavoriteData={residentCommunityData}
            // Pass the full favorites data from the optimized endpoint
            favoritesData={favorites}
          />

          <div className="pibPage-backToTop">
            <button type="button" className="pibPage-backToTop__button" onClick={handleBackToTopClick}>
              <span className="pibPage-backToTop__button__arrow" />
              <span className="pibPage-backToTop__button__text">Back to Top</span>
            </button>
          </div>

          <div className="pibPage-vectre">
            <div className="pibPage-vectre__message">
              Your Personalized Interactive Brochure is brought to you by
              {' '}
              <span>YOUR</span>
              TOUR, a sales enablement tool by THE VECTRE.
            </div>
            <div className="pibPage-vectre__colophon">
              Copyright ©
              {' '}
              {year}
              {' '}
              The Vectre - All Rights Reserved
            </div>
          </div>
        </div>
      </div>
    </FadingContainer>
  );
};

export default PibPage;
